import { Link as RouterLink } from "react-router-dom";
import { Link, Stack, Typography } from "@mui/material";
import { AxiosError } from "axios";
import { Fragment, useEffect, useState } from "react";
import { ApiRoutes } from "../../configuration/apiRoutes";
import { ClassifierRecordDetailsDto } from "../../models/ClassifierRecordDetailsDto";
import { RestService } from "../../utils/axios";
import KlasisLoader from "../KlasisLoader";
import { defaultLocale as defaultLocaleCode } from "../../constants/languages";
import { useParams } from "react-router-dom";
import { ClassifierRecordRelationDto } from "../../models/ClassifierRecordRelationDto";
import { useTranslation } from "react-i18next";
import ShareIcon from "@mui/icons-material/Share";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import { Translations } from "../../localization/translations";
import useKlasisSnackbar from "../../utils/klasisSnackbar";

interface IClassifierRecordDetailsProps {
    classifierId?: string;
    recordId?: number;
    prefetchedData?: ClassifierRecordDetailsDto;
}

const ClassifierRecordDetails: React.FC<IClassifierRecordDetailsProps> = (
    props: IClassifierRecordDetailsProps
) => {
    let { locale } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<ClassifierRecordDetailsDto | undefined>(
        props.prefetchedData
    );

    const loadData = (): void => {
        setIsLoading(true);

        RestService.Get<ClassifierRecordDetailsDto>(
            ApiRoutes.Classifiers.GetRecordDetails({
                classifierId: props.classifierId ?? "",
                localeCode: locale ?? defaultLocaleCode,
                recordId: props.recordId ?? 0,
            }),
            (response: ClassifierRecordDetailsDto) => {
                setData(response);
            },
            (error: AxiosError) => {
                console.log("Error: " + error.message);
            },
            () => {
                setIsLoading(false);
            }
        );
    };

    useEffect(() => {
        if (!data && props.classifierId && props.recordId) loadData();
    }, []);

    useEffect(() => window.scrollTo(0, 0), []);

    if (isLoading) {
        return <KlasisLoader />;
    }

    return (
        <>
            {data?.fields.map((field, index) => (
                <Fragment key={field.name}>
                    <Typography variant="caption">{field.name}</Typography>
                    <Typography
                        color="text.secondary"
                        sx={{
                            display: "block",
                            marginBottom: "1rem",
                            whiteSpace: "pre-wrap"
                        }}
                    >
                        {field.content}
                    </Typography>
                </Fragment>
            ))}

            <RelatedClassifiers
                related={data?.relations ?? []}
                classifierLocale={locale ?? defaultLocaleCode}
            />
        </>
    );
};

interface IRelatedClassifiersProps {
    related: ClassifierRecordRelationDto[];
    classifierLocale: string;
}

const RelatedClassifiers: React.FC<IRelatedClassifiersProps> = ({
    related,
    classifierLocale,
}) => {
    const { t } = useTranslation();
    const { klasisEnqueueSnackbar } = useKlasisSnackbar();

    if (related.length === 0) {
        return null;
    }

    return (
        <Stack
            style={{
                borderLeftWidth: 2,
                borderLeftStyle: "solid",
            }}
            sx={(theme) => ({
                padding: 1,
                borderLeftColor: theme.customComponents.infoBox.accentColor,
                background: theme.customComponents.infoBox.backgroundColor,
            })}
        >
            <Stack
                direction="row"
                spacing={2}
                sx={(theme) => ({
                    color: theme.customComponents.infoBox.accentColor,
                })}
            >
                <ShareIcon fontSize="small" />
                <Typography>
                    {t(Translations.classifier.relatedBlock.title)}
                </Typography>
            </Stack>
            <Stack
                sx={{
                    paddingLeft: 5,
                }}
                spacing={1}
            >
                {related.map((item) => (
                    <Stack key={item.id}>
                        {item.isClassifierPublished ? (
                            <Link
                                component={RouterLink}
                                to={`/${classifierLocale}/classifications/${item.klasisCode}/${item.code}?classifier-locale=${item.languageCode}`}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Typography>
                                        {item.code}: {item.name}
                                    </Typography>
                                    <ArrowCircleUpIcon
                                        style={{
                                            transform: "rotate(90deg)",
                                        }}
                                    />
                                </Stack>
                            </Link>
                        ) : (
                            <Link
                                style={{
                                    cursor: "pointer",
                                }}
                                onClick={() => {
                                    klasisEnqueueSnackbar(
                                        t(
                                            Translations.classifier.text
                                                .relatedNotPublished
                                        )
                                    );
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Typography>
                                        {item.code}: {item.name}
                                    </Typography>
                                    <ArrowCircleUpIcon
                                        style={{
                                            transform: "rotate(90deg)",
                                        }}
                                    />
                                </Stack>
                            </Link>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            {item.classifierCode ? `${item.classifierCode}: ` : ""}{item.classifierName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {item.relationType}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
};

export default ClassifierRecordDetails;
